import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { User, HelpCircle, LogOut, Crown } from "lucide-react";
import LanguageSwitcher from "./LanguageSwitcher";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery } from "@apollo/client";
import { useMyDataQuery } from "@/generated/graphql";
import { GET_COURSE_BY_SLUG } from "@/queries/courses";
import type { Course } from "@/types/course";
import { hasCourseAccess } from "@/lib/courses/hasCourseAccess";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { SidebarTrigger } from "@/components/ui/sidebar";
import AccountSettings from "./AccountSettings";
import CommunityModal from "./CommunityModal";
import {
  parseOpenAccountSettingsDetail,
  type AccountSettingsSocialPlatform,
} from "@/lib/account-settings/openAccountSettingsEvent";
import { useViralizeQualification } from "@/contexts/viralizeQualificationContext";
import { AssistantToggleButton } from "@/components/assistant/AssistantToggleButton";
import { useAssistantPanel } from "@/contexts/AssistantContext";
import { useAssistantAvailable } from "@/hooks/use-assistant-available";

interface HeaderProps {
  onStartTour?: () => void;
}

const Header = ({ onStartTour }: HeaderProps) => {
  const { t } = useTranslation();
  const [showAccountSettings, setShowAccountSettings] = useState(false);
  const [accountSettingsFocus, setAccountSettingsFocus] = useState<{
    platform: AccountSettingsSocialPlatform | null;
    requestKey: number;
  }>({ platform: null, requestKey: 0 });
  const [showCommunityModal, setShowCommunityModal] = useState(false);
  const { studioChatMounted } = useAssistantPanel();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { requestViralizeCourse } = useViralizeQualification();

  useEffect(() => {
    const openAccountSettings = (event: Event) => {
      const target = parseOpenAccountSettingsDetail(
        event instanceof CustomEvent ? event.detail : null,
      );
      if (!target) return;
      setAccountSettingsFocus((current) => ({
        platform: target.focusSocialPlatform,
        requestKey: current.requestKey + 1,
      }));
      setShowAccountSettings(true);
    };
    window.addEventListener("autoclipper:open-account-settings", openAccountSettings);
    return () => {
      window.removeEventListener("autoclipper:open-account-settings", openAccountSettings);
    };
  }, []);

  // Use Apollo GraphQL hook to fetch user data
  const { data: myData, loading: isMyDataLoading } = useMyDataQuery();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Use data from Apollo query, fallback to "Usuário" if loading or no data
  const userData = myData?.myData;
  const assistantAvailable = useAssistantAvailable(userData?.subscription?.plan?.features, !!userData);

  const { data: courseData } = useQuery<{ course: Course | null }>(GET_COURSE_BY_SLUG, {
    variables: { slug: "viralize-em-4-semanas" },
  });
  const courseAccess = hasCourseAccess({
    ownedByViewer: !!courseData?.course?.ownedByViewer,
    isAdmin: userData?.is_admin === true,
  });
  const displayName = userData?.name || userData?.email || t('headerExtra.defaultUser');
  const profilePicture = userData?.profile_picture;
  const showUserLoading = isMyDataLoading && !userData;

  return (
    <>
      <AccountSettings
        isOpen={showAccountSettings}
        onClose={() => {
          setShowAccountSettings(false);
          setAccountSettingsFocus((current) => ({ ...current, platform: null }));
        }}
        focusSocialPlatform={accountSettingsFocus.platform}
        focusRequestKey={accountSettingsFocus.requestKey}
      />
      <CommunityModal
        isOpen={showCommunityModal}
        onClose={() => setShowCommunityModal(false)}
        videoSrc="/videos/community-video.mp4"
        formUrl="https://form.typeform.com/to/dMu6D1On"
      />
    <div className="h-16 bg-card border-b border-border px-3 sm:px-4 lg:px-6 flex items-center justify-between gap-2 lg:gap-4 overflow-hidden">
      <div className="flex items-center gap-2 lg:gap-4 min-w-0">
        <SidebarTrigger className="md:hidden" />
        {!isMyDataLoading && courseAccess && (
          <button
            type="button"
            data-viralize-course-entry
            onClick={(event) => {
              void requestViralizeCourse(event.currentTarget);
            }}
            className="inline-flex min-w-0 items-center gap-1.5 rounded-full bg-gradient-primary text-primary-foreground font-semibold px-3 py-1.5 md:px-4 md:py-2 text-xs sm:text-sm shadow-lg shadow-primary/40 ring-2 ring-primary/60 hover:shadow-primary/60 hover:ring-primary transition-all"
          >
            <Crown className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">
              <span className="sm:hidden">{t("header.viralizeCourseCtaShort")}</span>
              <span className="hidden sm:inline">{t("header.viralizeCourseCta")}</span>
            </span>
          </button>
        )}
        {userData?.onboarding_objective === 'other_channels' ? (
          <a
            href="https://chat.whatsapp.com/BV9LMJnV2gV5DQbpt5ZNoA?s=cl&p=i&mlu=4"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-xl border-2 border-dashed border-primary/50 hover:border-primary hover:bg-primary/5 px-1.5 py-1.5 md:px-2.5 transition-all"
          >
            <div className="w-7 h-7 md:w-8 md:h-8 rounded-lg bg-white flex-shrink-0 overflow-hidden p-0.5">
              <img src="/images/the-clippers-logo.png" alt="The Clippers" className="w-full h-full object-contain" />
            </div>
            {/* With the course pill alongside it, the phone header only has room for the logo. */}
            <div className={cn("leading-tight", courseAccess && "hidden sm:block")}>
              <p className="font-bold text-xs text-foreground">The Clippers</p>
              <p className="hidden md:block text-[10px] text-muted-foreground">Comunidade oficial no WhatsApp</p>
            </div>
            <span className={cn("text-xs font-semibold text-primary ml-1", courseAccess && "hidden sm:inline")}>Entrar</span>
          </a>
        ) : (
          !isMyDataLoading && !courseAccess && (
            <button
              type="button"
              data-viralize-course-entry
              onClick={(event) => {
                void requestViralizeCourse(event.currentTarget);
              }}
              className="inline-flex min-w-0 items-center gap-1.5 rounded-full bg-gradient-primary text-primary-foreground font-semibold px-3 py-1.5 text-xs sm:text-sm shadow-lg shadow-primary/20 hover:shadow-primary/30 transition-shadow"
            >
              <Crown className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="truncate">
                <span className="sm:hidden">{t("header.viralizeCourseCtaShort")}</span>
                <span className="hidden sm:inline">{t("header.viralizeCourseCta")}</span>
              </span>
            </button>
          )
        )}
      </div>

      {/* Right - User */}
      <div className="flex items-center gap-1 sm:gap-2 lg:gap-4 min-w-0">
        {assistantAvailable && !studioChatMounted && <AssistantToggleButton />}
        <LanguageSwitcher />

        {onStartTour && (
          <Button id="help-button" variant="ghost" size="sm" onClick={onStartTour} className="tour-help-button">
            <HelpCircle className="w-3 h-3 sm:w-4 sm:h-4" />
          </Button>
        )}

        <div className="flex items-center gap-1 sm:gap-2">
          {showUserLoading ? (
            <div
              data-testid="header-user-loading"
              role="status"
              aria-label={t('common.loading')}
              className="flex items-center gap-1 sm:gap-2"
            >
              <Skeleton className="h-6 w-6 sm:h-8 sm:w-8 rounded-full" />
              <Skeleton className="hidden sm:block h-4 w-24" />
            </div>
          ) : (
          <div
            className="flex items-center gap-1 sm:gap-2 cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => {
              setAccountSettingsFocus((current) => ({
                platform: null,
                requestKey: current.requestKey + 1,
              }));
              setShowAccountSettings(true);
            }}
          >
            <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gradient-primary rounded-full flex items-center justify-center overflow-hidden">
              {profilePicture ? (
                <img
                  src={profilePicture}
                  alt={userData?.name || "User"}
                  className="w-full h-full object-cover"
                />
              ) : userData?.name ? (
                <span className="text-background text-xs sm:text-sm font-medium">
                  {getInitials(userData.name)}
                </span>
              ) : (
                <User className="w-3 h-3 sm:w-4 sm:h-4 text-background" />
              )}
            </div>
            <span className="hidden sm:inline text-xs sm:text-sm font-medium truncate">{displayName}</span>
          </div>
          )}

          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            className="text-red-500 hover:text-red-600 hover:bg-red-50 p-1 sm:p-2"
            title={t('header.logout')}
          >
            <LogOut className="w-3 h-3 sm:w-4 sm:h-4" />
          </Button>
        </div>
      </div>
    </div>
    </>
  );
};

export default Header;
